/**
 * Login Screen 2: Password Entry
 *
 * Second screen for password-based authentication
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

interface LoginScreen2PasswordProps {
  identifier: string;
  onSubmit: (password: string) => Promise<void>;
  onForgotPassword?: () => void;
  error: string | null;
}

export function LoginScreen2Password({
  identifier,
  onSubmit,
  onForgotPassword,
  error,
}: LoginScreen2PasswordProps) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSubmit(password);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" component="h1" gutterBottom>
          Enter Password
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Logging in as: {identifier}
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            type="password"
            label="Password"
            variant="outlined"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            autoFocus
            required
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={loading || !password}
          >
            {loading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </Button>

          {onForgotPassword && (
            <Button
              variant="text"
              size="small"
              onClick={onForgotPassword}
              disabled={loading}
            >
              Forgot Password?
            </Button>
          )}
        </Stack>
      </form>
    </Stack>
  );
}
